<template>
  <div class="landing">
    <!-- Hero 区域 -->
    <div class="hero">
      <div class="hero-inner">
        <div class="hero-text">
          <div class="wallet-logo">💰</div>
          <h1 class="hero-title">钱包支付</h1>
          <p class="hero-sub">请使用钱包应用扫描下方二维码完成支付</p>
        </div>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content">
      <!-- 加载中 -->
      <div v-if="loading">
        <div class="card">
          <div class="card-body center loading-wrap">
            <div class="loader"></div>
            <p class="loading-text">正在加载支付信息...</p>
          </div>
        </div>
      </div>

      <!-- 错误提示 -->
      <div v-else-if="error">
        <div class="card">
          <div class="card-body center">
            <span class="badge">提示</span>
            <p class="error-text">{{ error }}</p>
          </div>
        </div>
      </div>

      <!-- 已支付提示 -->
      <div v-else-if="paid">
        <div class="card">
          <div class="card-body center">
            <div class="success-icon">✅</div>
            <h3 class="result-title">订单已支付</h3>
            <p class="result-sub">请勿重复支付</p>
          </div>
        </div>
      </div>

      <!-- 已超时提示 -->
      <div v-else-if="expired">
        <div class="card">
          <div class="card-body center">
            <div class="timeout-icon">⏰</div>
            <h3 class="result-title">订单已超时</h3>
            <p class="result-sub">请返回并重新发起支付</p>
          </div>
        </div>
      </div>

      <!-- 支付信息 -->
      <div v-else class="row">
        <div class="col-left">
          <div class="card info-card">
            <div class="card-header info-header">
              <span>订单支付信息</span>
              <span class="countdown" v-if="countdownSeconds > 0">
                剩余支付时间：
                <span class="countdown-time">{{ formattedCountdown }}</span>
              </span>
              <span class="countdown ended" v-else>支付倒计时已结束</span>
            </div>
            <div class="card-body">
              <div class="form">
                <div class="form-item">
                  <label class="form-label">订单号</label>
                  <div class="input-block field-row">
                    <span class="field-value mono">
                      {{ data.merchantOrderNo }}
                    </span>
                    <button
                      class="copy-btn"
                      @click="copyText(data.merchantOrderNo)"
                    >
                      复制
                    </button>
                  </div>
                </div>
                <div class="form-item amount-item">
                  <label class="form-label">支付金额</label>
                  <div class="input-block field-row">
                    <span class="field-value amount">
                      ¥{{ data.paymentAmount }}
                    </span>
                    <button
                      class="copy-btn"
                      @click="copyText(data.paymentAmount)"
                    >
                      复制
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-right">
          <div class="card qrcode-card">
            <div class="card-header">扫码支付</div>
            <div class="card-body qrcode-body">
              <div class="qrcode-wrapper">
                <!-- 如果后端返回 base64 图片，直接显示 -->
                <img 
                  v-if="qrCodeImage" 
                  :src="qrCodeImage" 
                  alt="支付二维码" 
                  class="qrcode-img"
                />
                <!-- 如果需要生成二维码，使用这个容器 -->
                <div v-else id="qrcode" ref="qrcode"></div>
              </div>
              <p class="qrcode-tip">使用钱包应用扫描二维码完成支付</p>
            </div>
          </div>

          <div class="card side-card">
            <div class="card-header">温馨提示</div>
            <div class="card-body">
              <ul class="timeline">
                <li class="timeline-item">
                  <span class="timeline-icon">📱</span>
                  <div class="timeline-content">
                    <h3 class="timeline-title">扫码支付</h3>
                    <p>打开钱包应用，扫描上方二维码即可完成支付。</p>
                  </div>
                </li>
                <li class="timeline-item">
                  <span class="timeline-icon">⏰</span>
                  <div class="timeline-content">
                    <h3 class="timeline-title">支付时效</h3>
                    <p>请在倒计时结束前完成支付，超时订单将自动取消。</p>
                  </div>
                </li>
                <li class="timeline-item">
                  <span class="timeline-icon">🔒</span>
                  <div class="timeline-content">
                    <h3 class="timeline-title">安全提醒</h3>
                    <p>请确认支付金额和订单号，谨防诈骗。</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import apiConfig from "@/config/api";
import QRCode from "qrcodejs2";

export default {
  name: "WalletTransfer",
  data() {
    return {
      data: {},
      loading: true,
      error: null,
      countdownSeconds: 0,
      countdownTimerId: null,
      expired: false,
      paid: false,
      qrcodeInstance: null,
      qrCodeImage: null // base64 图片数据
    };
  },
  methods: {
    copyText(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      if (window.layui?.layer) {
        window.layui.layer.msg("已复制到剪贴板", { icon: 1, time: 1500 });
      } else {
        alert("✅ 已复制到剪贴板");
      }
    },
    startCountdown(totalSeconds) {
      this.clearCountdown();
      this.countdownSeconds = totalSeconds;
      this.countdownTimerId = setInterval(() => {
        if (this.countdownSeconds > 0) {
          this.countdownSeconds--;
          if (this.countdownSeconds === 0) {
            this.onCountdownEnd();
          }
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimerId) {
        clearInterval(this.countdownTimerId);
        this.countdownTimerId = null;
      }
    },
    onCountdownEnd() {
      this.expired = true;
    },
    generateQRCode(qrCodeUrl) {
      if (!qrCodeUrl) {
        return;
      }

      // 清除之前的二维码
      if (this.$refs.qrcode) {
        this.$refs.qrcode.innerHTML = "";
      }
      
      // 确保 DOM 已渲染
      this.$nextTick(() => {
        if (!this.$refs.qrcode) {
          console.error("二维码容器元素不存在");
          return;
        }

        try {
          this.qrcodeInstance = new QRCode(this.$refs.qrcode, {
            text: qrCodeUrl,
            width: 256,
            height: 256,
            colorDark: "#000000",
            colorLight: "#ffffff",
            correctLevel: QRCode.CorrectLevel.H
          });

          // 隐藏 canvas，仅显示 img（移动端兼容性）
          this.$nextTick(() => {
            const canvas = this.$refs.qrcode.querySelector('canvas');
            if (canvas) {
              canvas.style.display = 'none';
            }
          });
        } catch (error) {
          console.error("生成二维码失败:", error);
        }
      });
    }
  },
  created() {
    const merchantOrderNo = this.$route.params.merchantOrderNo;

    // 获取订单详情
    axios.get(apiConfig.getCommonInterfaceUrl(), {
      params: { merchantOrderNo }
    })
      .then((res) => {
        if (res.data.code === 200) {
          this.data = res.data.data;
          if (Number(this.data.order_status) === -1) {
            this.expired = true;
          } else {
            const expireTime = new Date(this.data.expire_time).getTime();
            const nowTime = Date.now();
            const diffSeconds = Math.floor((expireTime - nowTime) / 1000);
            if (diffSeconds > 0) {
              this.startCountdown(diffSeconds);
            } else {
              this.expired = true;
            }
          }

          // 处理二维码显示
          // 优先使用后端返回的 base64 图片
          if (this.data.payCode) {
            // 如果是 base64 数据，直接使用
            this.qrCodeImage = `data:image/png;base64,${this.data.payCode}`;
          } else if (this.data.qrCodeUrl || this.data.paymentUrl) {
            // 如果没有 base64，使用 URL 生成二维码
            const qrCodeUrl = this.data.qrCodeUrl || this.data.paymentUrl;
            this.$nextTick(() => {
              this.generateQRCode(qrCodeUrl);
            });
          }
        } else {
          this.error = res.data.msg || "订单获取失败";
        }
      })
      .catch(() => {
        this.error = "请求失败，无法获取订单信息";
      })
      .finally(() => {
        this.loading = false;
      });
  },
  computed: {
    formattedCountdown() {
      const mins = Math.floor(this.countdownSeconds / 60);
      const secs = this.countdownSeconds % 60;
      return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
  },
  beforeDestroy() {
    this.clearCountdown();
    if (this.$refs.qrcode) {
      this.$refs.qrcode.innerHTML = "";
    }
  }
};
</script>

<style scoped>
/* 钱包风格布局 */
.landing {
  background-color: #f5f5f7;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif;
}

.hero {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  padding: 60px 0 40px;
  text-align: center;
  border-bottom: none;
}

.hero-inner {
  max-width: 980px;
  margin: 0 auto;
  padding: 0 22px;
}

.hero-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wallet-logo {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 48px;
  font-weight: 700;
  color: #fff;
}

.hero-title {
  font-size: 48px;
  font-weight: 600;
  margin: 0 0 12px;
  letter-spacing: -0.015em;
  color: #fff;
}

.hero-sub {
  font-size: 21px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.content {
  margin: 40px auto 80px;
  max-width: 980px;
  padding: 0 22px;
}

/* 卡片样式 */
.card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.08);
  border: 1px solid #f5f5f7;
  overflow: hidden;
  margin-bottom: 24px;
}

.card-header {
  background: transparent;
  border: none;
  padding: 28px 28px 0;
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
}

.info-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.countdown {
  font-size: 14px;
  color: #10b981;
  font-weight: 400;
}

.countdown-time {
  color: #ff4d4f;
  font-weight: 600;
}

.countdown.ended {
  color: #d70015;
}

.card-body {
  padding: 0 28px 28px;
}

/* 分栏布局 */
.row {
  display: flex;
  gap: 30px;
}

.col-left {
  flex: 0 0 58.333333%;
  max-width: 58.333333%;
}

.col-right {
  flex: 0 0 41.666667%;
  max-width: 41.666667%;
}

/* 表单项样式 */
.form {
  margin: 0;
}

.form-item {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f5f5f7;
  display: flex;
  align-items: center;
  gap: 0;
}

.form-item:first-child {
  margin-top: 0;
  padding-top: 8px;
}

.form-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.form-label {
  font-size: 15px;
  font-weight: 500;
  color: #86868b;
  width: 90px;
  min-width: 90px;
  text-align: left;
  padding: 0;
  line-height: 22px;
  margin: 0;
  margin-right: 8px;
  background: none;
  border: none;
  display: flex;
  align-items: center;
}

.input-block {
  margin-left: 0;
  width: auto;
  flex: 1;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  margin: 0;
  flex: 1;
  min-width: 0;
}

.field-value {
  font-size: 17px;
  color: #1d1d1f;
  flex: 1;
  min-width: 100px;
  word-break: break-all;
  line-height: 22px;
  margin: 0;
  padding: 0;
  height: 22px;
  display: inline-block;
}

.field-value.mono {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
}

.field-value.amount {
  font-size: 20px;
  font-weight: 600;
  color: #10b981;
}

/* 按钮样式 */
.copy-btn {
  min-width: 80px;
  background: #10b981;
  border: none;
  border-radius: 980px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  padding: 6px 16px;
  transition: all 0.2s ease;
  line-height: 22px !important;
  height: 34px;
  white-space: nowrap;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
}

.copy-btn:hover {
  background: #059669;
  transform: scale(1.05);
}

.copy-btn:active {
  transform: scale(0.98);
}

/* 二维码卡片样式 */
.qrcode-card {
  margin-bottom: 24px;
}

.qrcode-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px;
}

.qrcode-wrapper {
  background: #ffffff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

#qrcode {
  display: flex;
  justify-content: center;
  align-items: center;
}

#qrcode img {
  display: block;
  max-width: 100%;
  height: auto;
}

.qrcode-img {
  display: block;
  width: 256px;
  height: 256px;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}

.qrcode-tip {
  color: #6e6e73;
  font-size: 14px;
  text-align: center;
  margin: 0;
}

/* 加载与错误 */
.center {
  text-align: center;
}

.loading-wrap {
  padding: 60px 0;
}

.loading-text {
  color: #86868b;
  font-size: 17px;
  margin-top: 16px;
}

.error-text {
  color: #d70015;
  margin-top: 16px;
  font-size: 17px;
}

.success-icon,
.timeout-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.result-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px;
}

.result-sub {
  color: #666;
  margin: 0;
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  background: #10b981;
  color: #fff;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 8px;
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid #f5f5f7;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 右侧提示卡片样式 */
.side-card .timeline {
  padding: 0;
  list-style: none;
  margin: 0;
}

.side-card .timeline-item {
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f5f5f7;
  display: flex;
  align-items: flex-start;
}

.side-card .timeline-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.timeline-icon {
  font-size: 20px;
  flex-shrink: 0;
  margin-right: 12px;
  margin-top: 2px;
}

.timeline-content {
  flex: 1;
}

.timeline-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 6px;
}

.timeline-content p {
  margin: 0;
  font-size: 14px;
  color: #6e6e73;
  line-height: 1.5;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .landing {
    padding-bottom: env(safe-area-inset-bottom);
  }

  .info-header {
    flex-direction: row;
    align-items: center;
    gap: 12px;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .countdown {
    font-size: 12px;
    margin-left: auto;
  }

  .hero {
    padding: 40px 0 24px;
  }

  .hero-title {
    font-size: 28px;
  }

  .hero-sub {
    font-size: 15px;
  }

  .content {
    margin: 24px auto 0;
    padding: 0 20px;
    padding-bottom: max(40px, env(safe-area-inset-bottom) + 20px);
  }

  .row {
    flex-direction: column;
    gap: 0;
  }

  .col-left,
  .col-right {
    flex: 1;
    max-width: 100%;
  }

  .card-header {
    padding: 16px 16px 0;
    font-size: 15px;
  }

  .card-body {
    padding: 0 16px 16px;
  }

  .qrcode-body {
    padding: 20px;
  }

  .qrcode-wrapper {
    padding: 16px;
  }

  .field-row {
    gap: 8px;
    flex-wrap: nowrap;
  }

  .field-value {
    font-size: 16px;
    min-width: 0;
    flex: 1;
  }

  .field-value.mono {
    font-size: 16px;
  }

  .field-value.amount {
    font-size: 18px;
  }

  .copy-btn {
    min-width: 80px;
    flex-shrink: 0;
  }

  .form-item {
    margin-bottom: 12px;
    padding-bottom: 12px;
  }

  .form-item:first-child {
    padding-top: 4px;
  }

  .card {
    margin-bottom: 16px;
  }

  .card:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .info-card {
    margin-bottom: 12px;
  }

  .qrcode-card {
    margin-bottom: 16px;
  }

  .side-card {
    margin-top: 16px;
  }

  .side-card .timeline-item {
    padding-bottom: 12px;
    margin-bottom: 12px;
  }

  .side-card .timeline-title {
    font-size: 14px;
    margin: 0 0 4px;
  }

  .side-card .timeline-content p {
    font-size: 13px;
  }
}

/* 极窄屏处理 */
@media (max-width: 360px) {
  .hero-title {
    font-size: 28px;
  }

  .hero-sub {
    font-size: 15px;
  }
}
</style>

